"use strict";
/**
 * Resolving a cleaning type from whatever the wire happens to carry.
 *
 * The legacy contract transports the cleaning type as its *price*
 * (`cleaning_type: "128"`) and recovers the name with
 * `switch (Number(value)) { case table.standard: ... default: throw }`.
 * That has three failure modes this module removes:
 *
 *   1. a browser holding a cached bundle sends last week's price and gets a
 *      500 `invalid cleaning type` at checkout;
 *   2. a decimal price matches no case and throws;
 *   3. two cleaning types can never share a price.
 *
 * `resolveCleaningType` accepts ids, legacy names, and price strings from *any*
 * rate card, so old clients keep working while new ones send ids. It only fails
 * when the value genuinely identifies nothing.
 */
Object.defineProperty(exports, "__esModule", { value: true });
exports.includesFreeExtras = exports.toLabel = exports.toLegacyName = exports.resolveCleaningTypeOrThrow = exports.resolveCleaningType = exports.UnknownCleaningTypeError = void 0;
const rates_1 = require("./rates");
const money_1 = require("./money");
const BY_LEGACY_NAME = new Map();
for (const id of rates_1.CLEANING_TYPE_IDS) {
    BY_LEGACY_NAME.set(id, id);
    BY_LEGACY_NAME.set(rates_1.CLEANING_TYPE_LEGACY_NAMES[id].toLowerCase(), id);
    BY_LEGACY_NAME.set(rates_1.CLEANING_TYPE_LABELS[id].toLowerCase(), id);
}
/**
 * Every price any rate card has ever charged, mapped to its cleaning type.
 *
 * Built across all cards on purpose: a client that sends a price from an older
 * card is out of date, not wrong, and should be resolved rather than rejected.
 *
 * This map is a *fallback only*, because prices are genuinely ambiguous across
 * cards — $228 was V1 Super Clean and later V2 Move In / Out, and $248 was V2
 * Super Clean and later V3 Move In / Out. Resolving by price alone would
 * relabel historical Super Clean bookings as Move In / Out, which also flips
 * their free-extras rule. Always prefer the card in force for the booking.
 */
const BY_PRICE_CENTS = new Map();
for (const card of rates_1.RATE_CARDS) {
    for (const id of rates_1.CLEANING_TYPE_IDS) {
        BY_PRICE_CENTS.set(card.cleaningTypes[id], id);
    }
}
class UnknownCleaningTypeError extends Error {
    constructor(value) {
        super(`Could not resolve a cleaning type from ${JSON.stringify(value)}. ` +
            `Expected one of ${rates_1.CLEANING_TYPE_IDS.join(", ")}, a legacy name, or a known price.`);
        this.name = "UnknownCleaningTypeError";
    }
}
exports.UnknownCleaningTypeError = UnknownCleaningTypeError;
/**
 * Resolve a cleaning type from an id, a legacy name, or a price.
 *
 * Returns `null` rather than throwing so callers can decide whether an
 * unresolvable value is a validation error or a 500. Use
 * `resolveCleaningTypeOrThrow` when it must be present.
 */
function resolveCleaningType(value, createdAt) {
    var _a;
    if (value === null || value === undefined)
        return null;
    const raw = String(value).trim();
    if (!raw)
        return null;
    const byName = BY_LEGACY_NAME.get(raw.toLowerCase());
    if (byName)
        return byName;
    // A bare number is a price, in dollars, in the legacy wire format.
    const asNumber = Number(raw.replace(/[$,]/g, ""));
    if (!Number.isFinite(asNumber))
        return null;
    const cents = (0, money_1.dollarsToCents)(asNumber);
    // Prefer the card the booking was actually sold under. Only fall back to the
    // all-time map for a value that card does not recognise — that is a stale
    // client, which should still resolve rather than 500 at checkout.
    if (createdAt) {
        const card = (0, rates_1.getRateCard)(createdAt);
        for (const id of rates_1.CLEANING_TYPE_IDS) {
            if (card.cleaningTypes[id] === cents)
                return id;
        }
    }
    return (_a = BY_PRICE_CENTS.get(cents)) !== null && _a !== void 0 ? _a : null;
}
exports.resolveCleaningType = resolveCleaningType;
function resolveCleaningTypeOrThrow(value, createdAt) {
    const resolved = resolveCleaningType(value, createdAt);
    if (!resolved)
        throw new UnknownCleaningTypeError(value);
    return resolved;
}
exports.resolveCleaningTypeOrThrow = resolveCleaningTypeOrThrow;
/** The legacy lowercase name, for wire formats and DB rows that still expect it. */
function toLegacyName(id) {
    return rates_1.CLEANING_TYPE_LEGACY_NAMES[id];
}
exports.toLegacyName = toLegacyName;
/** The display label. */
function toLabel(id) {
    return rates_1.CLEANING_TYPE_LABELS[id];
}
exports.toLabel = toLabel;
/**
 * Whether a cleaning type is at or above the Move In / Out price on its card.
 *
 * This is the "extras are included" rule. It used to be written as a bare
 * numeric comparison against a truncated price (`parseInt(price) >= 273`),
 * which silently stops working the moment the threshold carries cents — the
 * customer is then charged up to $105 for extras that should be free, with no
 * error anywhere. Comparing resolved types on the same card cannot drift.
 */
function includesFreeExtras(cleaningType, createdAt) {
    const card = (0, rates_1.getRateCard)(createdAt);
    return card.cleaningTypes[cleaningType] >= card.cleaningTypes.move_in_out;
}
exports.includesFreeExtras = includesFreeExtras;
//# sourceMappingURL=cleaning-types.js.map